import { useState, useCallback, useRef } from "react";
import { API_BASE } from "../config";

export type UploadState = "idle" | "uploading" | "done" | "error";

const ACCEPT_REGEX =
  /^(image\/(jpeg|png|gif|webp)|video\/(mp4|webm|quicktime))$/i;

// Smaller chunks (256 KB) for mobile: iOS Safari can hit ~60s limit when reading Blob chunks;
// smaller chunks finish within that window on slow connections.
const CHUNK_SIZE = 256 * 1024; // 256 KB
const CHUNK_THRESHOLD = 5 * 1024 * 1024; // 5 MB — use chunked above this
const CHUNK_UPLOAD_TIMEOUT_MS = 120_000; // 2 min per chunk so slow mobile can complete

type UsePhotoUploadOptions = {
  onUploaded?: () => void;
};

export function usePhotoUpload(options: UsePhotoUploadOptions = {}) {
  const { onUploaded } = options;
  const [progress, setProgress] = useState(0);
  const [state, setState] = useState<UploadState>("idle");
  const [error, setError] = useState<string | null>(null);
  const lastAttemptFilesRef = useRef<File[] | null>(null);

  const uploadFileSingle = useCallback((file: File): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const formData = new FormData();
      formData.append("file", file);

      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          setProgress(Math.round((e.loaded / e.total) * 100));
        }
      });

      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          try {
            const data = JSON.parse(xhr.responseText);
            reject(new Error(data.error ?? "Upload fehlgeschlagen"));
          } catch {
            reject(new Error(`Upload fehlgeschlagen (${xhr.status})`));
          }
        }
      });

      xhr.addEventListener("error", () => reject(new Error("Netzwerkfehler")));
      xhr.addEventListener("abort", () => reject(new Error("Abgebrochen")));

      xhr.open("POST", `${API_BASE}/api/photos/upload`);
      xhr.withCredentials = true;
      xhr.send(formData);
    });
  }, []);

  const uploadFileChunked = useCallback(async (file: File): Promise<void> => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    const initRes = await fetch(`${API_BASE}/api/photos/upload/init`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        filename: file.name,
        totalSize: file.size,
        totalChunks,
      }),
    });
    if (!initRes.ok) {
      const data = await initRes.json().catch(() => ({}));
      throw new Error((data as { error?: string }).error ?? "Upload konnte nicht gestartet werden");
    }
    const { uploadId } = (await initRes.json()) as { uploadId: string };

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const blob = file.slice(start, end);
      // Materialize as ArrayBuffer: more reliable on mobile (avoids Blob read timeouts in iOS Safari).
      const body = await blob.arrayBuffer();

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        CHUNK_UPLOAD_TIMEOUT_MS
      );

      const chunkRes = await fetch(`${API_BASE}/api/photos/upload/chunk`, {
        method: "POST",
        credentials: "include",
        signal: controller.signal,
        headers: {
          "X-Upload-Id": uploadId,
          "X-Chunk-Index": String(i),
          "X-Total-Chunks": String(totalChunks),
        },
        body,
      });

      clearTimeout(timeoutId);

      if (!chunkRes.ok) {
        const data = await chunkRes.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error ?? "Teil-Upload fehlgeschlagen");
      }
      setProgress(Math.round(((i + 1) / totalChunks) * 100));
    }

    const completeController = new AbortController();
    const completeTimeoutId = setTimeout(
      () => completeController.abort(),
      CHUNK_UPLOAD_TIMEOUT_MS
    );
    const completeRes = await fetch(`${API_BASE}/api/photos/upload/complete`, {
      method: "POST",
      credentials: "include",
      signal: completeController.signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ uploadId }),
    });
    clearTimeout(completeTimeoutId);
    if (!completeRes.ok) {
      const data = await completeRes.json().catch(() => ({}));
      throw new Error((data as { error?: string }).error ?? "Upload-Abschluss fehlgeschlagen");
    }
  }, []);

  const uploadFile = useCallback(
    (file: File): Promise<void> => {
      return file.size > CHUNK_THRESHOLD
        ? uploadFileChunked(file)
        : uploadFileSingle(file);
    },
    [uploadFileSingle, uploadFileChunked]
  );

  const runUpload = useCallback(
    async (fileList: FileList | File[]) => {
      const files = (Array.isArray(fileList) ? fileList : Array.from(fileList)).filter(
        (f) => ACCEPT_REGEX.test(f.type)
      );
      if (files.length === 0) return;

      lastAttemptFilesRef.current = files;
      setState("uploading");
      setError(null);
      setProgress(0);

      try {
        for (const file of files) {
          await uploadFile(file);
        }
        lastAttemptFilesRef.current = null;
        setState("done");
        setProgress(100);
        onUploaded?.();
        setTimeout(() => {
          setState("idle");
          setProgress(0);
        }, 1500);
      } catch (err) {
        const message =
          err instanceof Error
            ? err.name === "AbortError"
              ? "Upload-Zeitüberschreitung. Bitte bessere Verbindung nutzen oder kleinere Datei wählen."
              : err.message
            : "Upload fehlgeschlagen";
        setError(message);
        setState("error");
      }
    },
    [uploadFile, onUploaded]
  );

  const retry = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      const files = lastAttemptFilesRef.current;
      if (files?.length) {
        lastAttemptFilesRef.current = null;
        setError(null);
        setProgress(0);
        runUpload(files);
      } else {
        setState("idle");
        setError(null);
        setProgress(0);
      }
    },
    [runUpload]
  );

  return { progress, state, error, runUpload, retry };
}

export const ACCEPT =
  "image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime";
